"use client";

import { cn } from "@/lib/utils";
import BadFaceIcon from "@public/svgs/face/bad.svg";
import GoodFaceIcon from "@public/svgs/face/good.svg";
import NormalFaceIcon from "@public/svgs/face/normal.svg";
import VeryBadFaceIcon from "@public/svgs/face/very-bad.svg";
import VeryGoodFaceIcon from "@public/svgs/face/very-good.svg";
import { Slider as SliderPrimitive, Tooltip } from "@repo/ui/components";
import { motion } from "framer-motion";
import * as React from "react";

export interface MissionLikertScaleProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

const SCALE_GUIDE_ASSETS = {
  1: { icon: VeryBadFaceIcon, label: "전혀 아니다" },
  2: { icon: BadFaceIcon, label: "아니다" },
  3: { icon: NormalFaceIcon, label: "보통이다" },
  4: { icon: GoodFaceIcon, label: "그렇다" },
  5: { icon: VeryGoodFaceIcon, label: "매우 그렇다" },
} as const;

export interface MissionThumbProps {
  value: number;
  className?: string;
  size?: number;
}

const MissionThumb = ({ value, className, size = 40 }: MissionThumbProps) => {
  const id = React.useId();
  const { icon: FaceIcon, label } = SCALE_GUIDE_ASSETS[value as 1 | 2 | 3 | 4 | 5];

  return (
    <div className="flex flex-col items-center justify-center">
      <motion.div
        animate={{
          y: [-12, 0, -12],
        }}
        data-tooltip-id={`tooltip-${id}`}
        transition={{
          duration: 1.2,
          ease: "easeInOut",
          repeat: Number.POSITIVE_INFINITY,
        }}
      >
        <Tooltip id={`tooltip-${id}`} placement="top">
          {label}
        </Tooltip>
      </motion.div>
      <div
        className={cn(
          "flex items-center justify-center text-violet-500 gap-6",
          "px-2 py-4",
          "bg-violet-500/20 rounded-sm",
          className,
        )}
      >
        <FaceIcon width={size} height={size} />
      </div>
    </div>
  );
};

const isMissionThumb = (child: React.ReactNode): child is React.ReactElement<MissionThumbProps> => {
  return React.isValidElement(child) && child.type === MissionThumb;
};

const MissionLikertScaleBase = ({
  value,
  onChange,
  min = 1,
  max = 5,
  step = 1,
  className,
  disabled = false,
  children,
}: MissionLikertScaleProps) => {
  const normalizedValue = (value - min) / (max - min);

  const dotPositions = React.useMemo(() => {
    const positions: number[] = [];
    for (let i = min; i <= max; i += step) {
      positions.push((i - min) / (max - min));
    }
    return positions;
  }, [min, max, step]);

  const thumbChild = React.Children.toArray(children).find(isMissionThumb);

  return (
    <div className={cn("flex w-full flex-col gap-5", className)}>
      <div className={cn("relative h-18 w-full px-11 pt-20")}>
        <SliderPrimitive.Root
          value={[value]}
          onValueChange={values => {
            const newValue = values[0];
            if (newValue !== undefined) {
              onChange(newValue);
            }
          }}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className="relative flex h-18 w-full touch-none select-none items-center"
        >
          <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-zinc-100">
            <SliderPrimitive.Range className="absolute h-full bg-violet-500" />
          </SliderPrimitive.Track>

          <div className="pointer-events-none absolute left-0 right-0 top-1/2 flex w-full -translate-y-1/2 items-center justify-between px-0">
            {dotPositions.map((position, index) => {
              const isActive = position <= normalizedValue;
              const isLarge = index === 0 || index === dotPositions.length - 1;
              const key = `dot-${position}-${index}`;

              return (
                <div
                  key={key}
                  className={cn(
                    "rounded-full transition-colors bg-violet-500",
                    isLarge ? "size-3" : "size-2",
                    isActive ? "opacity-100" : "opacity-20",
                  )}
                />
              );
            })}
          </div>

          <SliderPrimitive.Thumb
            className={cn(
              "relative block focus:outline-none disabled:pointer-events-none disabled:opacity-50",
              disabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing",
              !thumbChild &&
                "size-6 rounded-full bg-violet-500 shadow-lg ring-2 ring-offset-4 ring-violet-500",
            )}
          >
            {thumbChild && (
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                {React.cloneElement(thumbChild, { value })}
              </div>
            )}
          </SliderPrimitive.Thumb>
        </SliderPrimitive.Root>
      </div>
    </div>
  );
};

export const MissionLikertScale = Object.assign(MissionLikertScaleBase, { Thumb: MissionThumb });
